// Natural-language quick add on a Space and a Track (Kareem, 4 Nov): type "Send steering pack to Dr. Amir Thursday
// 3pm, owner Sara, at risk, deliverable, in Strategy track" and it is created as written. Under the line, chips show
// what it understood: title, kind, date and time, owners, status, track, milestone. The browser reads the line on
// every keystroke (parseQuickAdd); after a short pause the fast model reads it too (POST /spaces/:id/quick-add/parse,
// ids only from this space) and fills what the rules missed (those chips are violet: the assistant's reading, until you
// change them). Click a chip to change it, x removes it. Enter creates exactly what the chips show, through the same
// POST /activities as the New activity form (same permissions, same validation), and never waits on the model for more
// than a moment. Escape clears.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ACTIVITY_STATUSES, ACTIVITY_TYPES, ActivityCreate, STATUS_LABELS, TYPE_LABELS, can, mergeQuick, parseQuickAdd,
  type ActivityStatus, type ActivityType, type QuickAddDraft, type QuickField, type SessionUser, type SpaceDetail,
} from '@dpw/shared';
import { api } from '../api';
import { dayLabel, localDate, toIsoAt } from '../lib/format';
import { ErrorNote } from './bits';
import { IconPlus, IconX } from './icons';
import { teamOf } from './people';
import { useToast } from './toast';

/** The model is asked after this long without typing. */
const IDLE_MS = 400;
/** Enter waits at most this long for a reading already on its way. */
const ENTER_WAIT_MS = 800;

type Chip = Exclude<QuickField, 'title'> | 'title';
type Overrides = Partial<QuickAddDraft>;

const plusHour = (t: string) => { const [h, m] = t.split(':').map(Number); return `${String(Math.min(23, h! + 1)).padStart(2, '0')}:${String(m).padStart(2, '0')}`; };

/** The activity the chips describe, as the New activity form would send it. */
export function toCreate(d: QuickAddDraft, o: { defaultTrackId: string; me: SessionUser; today: string }): ActivityCreate {
  const type: ActivityType = d.type ?? 'task';
  const timed = type === 'meeting' || type === 'workshop';
  const date = d.date ?? (d.time ? o.today : null);
  const owners = d.ownerIds.length ? d.ownerIds : null;
  return {
    trackId: d.trackId ?? o.defaultTrackId, type, title: d.title.trim(),
    ...(owners ? { ownerIds: owners } : {}),
    ...(d.status ? { status: d.status } : {}),
    ...(d.isMilestone ? { isMilestone: true } : {}),
    startsAt: timed && date ? toIsoAt(date, d.time ?? '10:00') : null,
    endsAt: timed && date ? toIsoAt(date, plusHour(d.time ?? '10:00')) : null,
    dueDate: !timed ? date : null,
    dueTime: !timed && date && d.time ? d.time : null,
  };
}

export function QuickAdd({ space, me, trackId, label = 'Quick add an activity' }: { space: SpaceDetail; me: SessionUser; trackId: string; label?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState('');
  const [over, setOver] = useState<Overrides>({});
  const [model, setModel] = useState<{ text: string; draft: QuickAddDraft } | null>(null);
  const [editing, setEditing] = useState<Chip | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const inflight = useRef<{ text: string; p: Promise<void> } | null>(null);
  const seq = useRef(0);
  const input = useRef<HTMLInputElement>(null);

  const people = useMemo(() => teamOf(space, me), [space, me]);
  const tracks = useMemo(() => space.tracks.map(t => ({ id: t.id, name: t.name })), [space.tracks]);
  const today = localDate();
  const local = useMemo(() => parseQuickAdd(text, { today, tracks, people }), [text, today, tracks, people]);
  const fromModel = model && model.text === text.trim() ? model.draft : null;
  const merged = useMemo(() => mergeQuick(local, fromModel), [local, fromModel]);
  const draft: QuickAddDraft = { ...merged, ...over, ownerIds: over.ownerIds ?? merged.ownerIds };
  // A chip the model filled (and the rules did not, and nobody changed): violet.
  const byAi = (f: QuickField) => !!fromModel && !local.found.includes(f) && !(f in over || (f === 'owners' && 'ownerIds' in over) || (f === 'track' && 'trackId' in over) || (f === 'milestone' && 'isMilestone' in over));
  const latest = useRef(draft);
  latest.current = draft;

  // The model, after a pause in typing (its answer is used only while the line still says the same).
  useEffect(() => {
    const t = text.trim();
    if (t.length < 3) return;
    const id = window.setTimeout(() => {
      const n = ++seq.current;
      const p = api.spaces.quickParse(space.id, { text: t, trackId }).then(r => { if (n === seq.current) setModel({ text: t, draft: r.draft }); }, () => undefined)
        .finally(() => { if (inflight.current?.p === p) inflight.current = null; });
      inflight.current = { text: t, p };
    }, IDLE_MS);
    return () => window.clearTimeout(id);
  }, [text, space.id, trackId]);

  const clear = () => { setText(''); setOver({}); setModel(null); setEditing(null); setError(null); seq.current++; inflight.current = null; };

  const create = async () => {
    if (busy || !text.trim()) return;
    setError(null);
    // A reading of this very line on its way: wait a moment for it, never long.
    const f = inflight.current;
    if (f && f.text === text.trim()) await Promise.race([f.p, new Promise(r => window.setTimeout(r, ENTER_WAIT_MS))]);
    const d = latest.current;
    if (!d.title.trim()) { setError(new Error('Give it a title: what is it?')); return; }
    const body = toCreate(d, { defaultTrackId: trackId, me, today });
    const check = ActivityCreate.safeParse(body);
    if (!check.success) { setError(new Error(check.error.issues.map(i => `${i.path.join('.') || 'Activity'}: ${i.message}`).join('. '))); return; }
    setBusy(true);
    try {
      const a = await api.activities.create(check.data);
      await qc.invalidateQueries();
      const mine = a.owners.length === 1 && a.owners[0]!.id === me.id;
      toast(`Added "${a.title}" to ${a.trackName} as a ${TYPE_LABELS[a.type].toLowerCase()} for ${mine ? 'you' : a.owners.map(o => o.name.split(' ')[0]).join(' and ')}`);
      clear();
      input.current?.focus();
    } catch (e) { setError(e); } finally { setBusy(false); }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void create(); }
    else if (e.key === 'Escape' && (text || editing)) { e.preventDefault(); e.stopPropagation(); clear(); }
  };
  const set = (o: Overrides) => setOver(x => ({ ...x, ...o }));
  const remove = (c: Chip) => {
    setEditing(null);
    if (c === 'type') set({ type: null });
    else if (c === 'date') set({ date: null, time: null });
    else if (c === 'time') set({ time: null });
    else if (c === 'owners') set({ ownerIds: [] });
    else if (c === 'status') set({ status: null });
    else if (c === 'track') set({ trackId: null });
    else if (c === 'milestone') set({ isMilestone: false });
  };

  const trackName = (id: string | null) => tracks.find(t => t.id === (id ?? trackId))?.name ?? '';
  const personName = (id: string) => (id === me.id ? 'You' : people.find(p => p.id === id)?.name ?? 'Someone');
  const chips: { c: Chip; k: string; v: string; ai: boolean; removable: boolean }[] = [];
  if (text.trim()) {
    chips.push({ c: 'title', k: 'Title', v: draft.title || 'No title yet', ai: byAi('title') && draft.title !== local.draft.title, removable: false });
    chips.push({ c: 'type', k: 'Kind', v: TYPE_LABELS[draft.type ?? 'task'], ai: byAi('type'), removable: !!draft.type });
    if (draft.date) chips.push({ c: 'date', k: 'Date', v: dayLabel(draft.date), ai: byAi('date'), removable: true });
    if (draft.time) chips.push({ c: 'time', k: 'Time', v: draft.time, ai: byAi('time'), removable: true });
    chips.push({ c: 'owners', k: draft.ownerIds.length > 1 ? 'Owners' : 'Owner', v: draft.ownerIds.length ? draft.ownerIds.map(personName).join(', ') : 'You', ai: byAi('owners'), removable: draft.ownerIds.length > 0 });
    if (draft.status) chips.push({ c: 'status', k: 'Status', v: STATUS_LABELS[draft.status], ai: byAi('status'), removable: true });
    chips.push({ c: 'track', k: 'Track', v: trackName(draft.trackId), ai: byAi('track'), removable: !!draft.trackId && draft.trackId !== trackId });
    if (draft.isMilestone) chips.push({ c: 'milestone', k: 'Milestone', v: 'Yes', ai: byAi('milestone'), removable: true });
  }

  return (
    <div className="qaddwrap">
      <form className="qadd" onSubmit={e => { e.preventDefault(); void create(); }}>
        <IconPlus />
        <input ref={input} value={text} onChange={e => { setText(e.target.value); setError(null); }} onKeyDown={onKey}
          placeholder="Add an activity: try 'Workshop with IT Tuesday 10am, owner Ali'" aria-label={label} aria-describedby="qadd-chips" autoComplete="off" />
        <span className="xs mut">{busy ? <span className="spin" /> : !text ? `A task owned by you, no date, in ${trackName(null)}` : 'Enter to add'}</span>
      </form>
      {chips.length > 0 && (
        <div className="qchips" id="qadd-chips" role="group" aria-label="What it understood">
          {chips.map(ch => (
            <span key={ch.c} className={`qchip${ch.ai ? ' ai' : ''}${editing === ch.c ? ' on' : ''}`} data-chip={ch.c}>
              <button type="button" className="qv" onClick={() => setEditing(editing === ch.c ? null : ch.c)} aria-expanded={editing === ch.c} aria-label={`${ch.k}: ${ch.v}. Change`}>
                <span className="qk">{ch.k}</span>{ch.v}
              </button>
              {ch.removable && <button type="button" className="qx" aria-label={`Remove ${ch.k.toLowerCase()}`} onClick={() => remove(ch.c)}><IconX /></button>}
            </span>
          ))}
        </div>
      )}
      {editing && text.trim() && (
        <div className="qpick" role="group" aria-label={`Change ${editing}`} onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); input.current?.focus(); } }}>
          {editing === 'title' && <input className="inp" autoFocus aria-label="Title" value={draft.title} onChange={e => set({ title: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setEditing(null); input.current?.focus(); } }} />}
          {editing === 'type' && <div className="seg2">{ACTIVITY_TYPES.map(t => <button key={t} type="button" className={(draft.type ?? 'task') === t ? 'on' : ''} aria-pressed={(draft.type ?? 'task') === t} onClick={() => { set({ type: t }); setEditing(null); }}>{TYPE_LABELS[t]}</button>)}</div>}
          {editing === 'date' && <input className="inp" type="date" autoFocus aria-label="Date" value={draft.date ?? ''} onChange={e => set({ date: e.target.value || null })} />}
          {editing === 'time' && <input className="inp" type="time" autoFocus aria-label="Time" value={draft.time ?? ''} onChange={e => set({ time: e.target.value || null })} />}
          {editing === 'status' && <div className="seg2">{ACTIVITY_STATUSES.map(s => <button key={s} type="button" className={draft.status === s ? 'on' : ''} aria-pressed={draft.status === s} onClick={() => { set({ status: s as ActivityStatus }); setEditing(null); }}>{STATUS_LABELS[s]}</button>)}</div>}
          {editing === 'track' && (
            <select className="inp" autoFocus aria-label="Track" value={draft.trackId ?? trackId} onChange={e => { set({ trackId: e.target.value }); setEditing(null); }}>
              {tracks.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {editing === 'owners' && (
            <div className="qowners">
              {people.map(p => {
                const on = draft.ownerIds.includes(p.id) || (!draft.ownerIds.length && p.id === me.id);
                return (
                  <label key={p.id} className="qown"><input type="checkbox" checked={on} onChange={() => {
                    const cur = draft.ownerIds.length ? draft.ownerIds : can(me.role, 'activity.own') ? [me.id] : [];
                    set({ ownerIds: on ? cur.filter(x => x !== p.id) : [...cur, p.id] });
                  }} />{p.name}</label>
                );
              })}
            </div>
          )}
          {editing === 'milestone' && <span className="xs mut">A milestone. Press x on the chip to make it a normal activity.</span>}
        </div>
      )}
      {error ? <div style={{ padding: '0 16px 8px' }}><ErrorNote error={error} /></div> : null}
    </div>
  );
}
